import React, { useCallback, useEffect, useRef, useState } from "react";
import { parseFromLegacyPlaceResult } from "../utils/parseAddressFromGoogle";

const inputBase = {
  padding: "0.5rem",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontFamily: "inherit",
  backgroundColor: "#ffffff",
  color: "#333333",
  colorScheme: "light only",
  width: "100%",
  boxSizing: "border-box",
};

const linkStyle = {
  marginTop: "0.6rem",
  background: "none",
  border: "none",
  color: "#1565c0",
  cursor: "pointer",
  textDecoration: "underline",
  fontFamily: "Quicksand, sans-serif",
  fontSize: "0.9rem",
  padding: 0,
  display: "inline",
};

function waitForGoogleMaps(maxWaitMs = 10000) {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve();
        return;
      }
      if (Date.now() - start > maxWaitMs) {
        reject(new Error("Google Maps did not load"));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

/**
 * Classic Places Autocomplete (reliable) + manual fallback.
 * onChange(line, { rawAddress, street, city, state, zip, place, source: 'legacy' | 'manual' })
 */
export default function AddressAutocomplete({
  address = "",
  onChange,
  street: initialStreet = "",
  city: initialCity = "",
  state: initialState = "",
  zip: initialZip = "",
}) {
  const [manual, setManual] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [mStreet, setMStreet] = useState("");
  const [mCity, setMCity] = useState("");
  const [mState, setMState] = useState("");
  const [mZip, setMZip] = useState("");

  const inputRef = useRef(null);
  const acRef = useRef(null);
  const addressRef = useRef(address);
  addressRef.current = address;

  const emit = useCallback(
    (line, details) => {
      onChange(line, {
        rawAddress: details.rawAddress ?? line,
        street: details.street ?? "",
        city: details.city ?? "",
        state: details.state ?? "",
        zip: details.zip ?? "",
        place: details.place,
        source: details.source,
      });
    },
    [onChange]
  );

  useEffect(() => {
    if (manual) {
      setMStreet(initialStreet || "");
      setMCity(initialCity || "");
      setMState(initialState || "");
      setMZip(initialZip || "");
    }
  }, [manual, initialStreet, initialCity, initialState, initialZip]);

  useEffect(() => {
    if (manual) return;
    let cancelled = false;

    const cleanup = () => {
      if (window.google && window.google.maps && acRef.current) {
        window.google.maps.event.clearInstanceListeners(acRef.current);
      }
      acRef.current = null;
    };

    (async () => {
      setLoadError(null);
      try {
        await waitForGoogleMaps();
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            "Address search did not load. Please enter your address using the form below, or try refreshing the page."
          );
        }
        return;
      }
      if (cancelled) return;
      if (!inputRef.current) return;

      const g = window.google?.maps;
      if (!g?.places) {
        if (!cancelled) {
          setLoadError("Address search is unavailable. Please use manual entry below.");
        }
        return;
      }

      cleanup();
      const input = inputRef.current;
      input.value = addressRef.current || "";
      const ac = new g.places.Autocomplete(input, {
        componentRestrictions: { country: "us" },
        fields: ["address_components", "formatted_address", "geometry"],
      });
      acRef.current = ac;
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const p = parseFromLegacyPlaceResult(place);
        if (!p.rawAddress && input.value) {
          emit(input.value, {
            rawAddress: input.value,
            street: "",
            city: "",
            state: "",
            zip: "",
            place,
            source: "legacy",
          });
          return;
        }
        emit(p.rawAddress, { ...p, place, source: "legacy" });
      });
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [emit, manual]);

  useEffect(() => {
    if (!manual && inputRef.current) {
      inputRef.current.value = address;
    }
  }, [address, manual]);

  const pushManual = useCallback(() => {
    const line = [mStreet, mCity, mState, mZip].filter(Boolean).join(", ");
    emit(line, {
      rawAddress: line,
      street: mStreet,
      city: mCity,
      state: mState,
      zip: mZip,
      source: "manual",
    });
  }, [mStreet, mCity, mState, mZip, emit]);

  useEffect(() => {
    if (!manual) return;
    const id = setTimeout(pushManual, 0);
    return () => clearTimeout(id);
  }, [manual, pushManual, mStreet, mCity, mState, mZip]);

  if (manual) {
    return (
      <div>
        <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "0.75rem" }}>
          Enter your address as it appears on mail. Prefer search when it works; this is a fallback
          if suggestions do not load.
        </p>
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <input
            type="text"
            value={mStreet}
            onChange={(e) => setMStreet(e.target.value)}
            placeholder="Street address"
            style={inputBase}
            autoComplete="street-address"
          />
          <input
            type="text"
            value={mCity}
            onChange={(e) => setMCity(e.target.value)}
            placeholder="City"
            style={inputBase}
            autoComplete="address-level2"
          />
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <input
              type="text"
              value={mState}
              onChange={(e) => setMState(e.target.value)}
              placeholder="State"
              style={{ ...inputBase, flex: 1, minWidth: "100px" }}
              autoComplete="address-level1"
            />
            <input
              type="text"
              value={mZip}
              onChange={(e) => setMZip(e.target.value.replace(/[^\d-]/g, ""))}
              placeholder="ZIP"
              style={{ ...inputBase, flex: 1, minWidth: "100px" }}
              autoComplete="postal-code"
            />
          </div>
        </div>
        <button type="button" style={linkStyle} onClick={() => setManual(false)}>
          Back to address search
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", colorScheme: "light only" }}>
      {loadError && (
        <p style={{ color: "#b71c1c", fontSize: "0.9rem", marginBottom: "0.5rem" }} role="status">
          {loadError}
        </p>
      )}
      <label htmlFor="umss-address-search" style={{ display: "block", color: "#666", marginBottom: "0.35rem" }}>
        Search or type your address
      </label>
      <input
        id="umss-address-search"
        ref={inputRef}
        type="text"
        placeholder="Start typing — then pick a US address from the list"
        defaultValue={address}
        style={inputBase}
        autoComplete="off"
      />
      <p style={{ fontSize: "0.75rem", color: "#888", marginTop: "0.35rem" }}>
        Suggestions are powered by Google. Choose a result to confirm street, city, and ZIP; or
        use manual entry if needed.
      </p>
      <div style={{ marginTop: "0.5rem" }}>
        <button type="button" style={linkStyle} onClick={() => setManual(true)}>
          Can’t use search? Enter address manually
        </button>
      </div>
    </div>
  );
}
